import { useCallback, useState } from 'react';
import { clearDraft, readDraft, slideDraftKey } from '../../lib/draftStorage';
import { useDraft } from '../../lib/useDraft';
import type {
  LogoPosition,
  PictureTreatment,
  SlideBackground,
  SlideItemRead,
  SlideLayoutTemplate,
  SlideOverlayStyle,
  SlidePhotoBox,
  SlidePoint,
  SlideRead,
} from '../../types/api';
import { isPictureTreatment, readPictureTreatment } from '../generation/pictureStyles';
import { isSlidePattern } from './slideSources';
import { SLIDE_TEMPLATES } from './slideTemplates';

/**
 * Unsaved layer edits survive a refresh, a crash and a closed tab.
 *
 * The layer editor deliberately does *not* save on drop: a save is a re-render
 * on the server, so N nudges have to cost one render, not N. That leaves a
 * window where real work exists only in React state, and this closes it —
 * locally, with no network write anywhere, including on unload.
 *
 * A slide is not a form: it is a server row that gets re-rendered under the
 * editor. So the draft carries the `updated_at` it was edited against, and a
 * draft whose baseline no longer matches is dropped without a word — replaying
 * a caption over a slide that has since been re-themed or re-rendered would put
 * back work the user can no longer see the starting point of.
 */

/** Short enough to survive a crash, long enough not to write mid-word. */
const SLIDE_DRAFT_DEBOUNCE_MS = 400;

/** One item row as the draft stores it: the editor's strings, minus its key. */
export interface SlideDraftItem {
  title: string;
  subtitle: string;
  value: string;
  asset_path: string | null;
}

/**
 * Everything the layer editor holds that a refresh would otherwise lose.
 *
 * Field names mirror the editor's own state rather than the wire, because this
 * is a local snapshot of a form and never a request body. `asset_path` inside
 * an item is the exception: it is a storage key copied straight through.
 */
export interface SlideDraftForm {
  headline: string;
  body: string;
  eyebrow: string;
  overlayStyle: SlideOverlayStyle;
  cardEnabled: boolean;
  background: SlideBackground;
  logoPosition: LogoPosition;
  textPosition: SlidePoint | null;
  layoutTemplate: SlideLayoutTemplate;
  /** How the picture is presented; ignored by the renderer on a flat background. */
  pictureTreatment: PictureTreatment;
  /** The curated icon on the slide, or null for none. */
  icon: string | null;
  logoPoint: SlidePoint | null;
  /** The anniversary poster's photo box; null means the template's own. */
  photoBox: SlidePhotoBox | null;
  items: SlideDraftItem[];
  /**
   * A v2 community slide's text per text-box id, as typed. Empty on every
   * other slide.
   */
  templateFields: Record<string, string>;
}

/**
 * The form as it may sit in storage: a draft written before template fields,
 * treatments or icons existed has none of them, and reads back with the
 * slide's own values rather than being dropped.
 */
type StoredSlideDraftForm = Omit<SlideDraftForm, 'templateFields' | 'pictureTreatment' | 'icon'> & {
  templateFields?: Record<string, string>;
  pictureTreatment?: PictureTreatment;
  icon?: string | null;
};

/** What is written to storage: the form, and the slide version behind it. */
interface SlideDraftPayload {
  baseline: string;
  form: StoredSlideDraftForm;
}

/** A restored draft, or the slide itself when there was nothing to restore. */
export interface SlideDraftRestore {
  form: SlideDraftForm;
  /** ISO write time of the recovered draft. Null when none was applied. */
  savedAt: string | null;
}

export interface SlideDraftState {
  /** Write time while the recovery notice is still up; null once it is gone. */
  restoredAt: string | null;
  /** Hides the notice. The recovered work stays in the form. */
  dismiss: () => void;
  /** Throws the stored draft away; the caller resets the form to the slide. */
  discard: () => void;
  /** Retires the draft once a save has written the work to the server. */
  clear: () => void;
}

/** Exhaustive by construction: a new arm of either union breaks the build. */
const LOGO_POSITIONS: Record<LogoPosition, true> = {
  top_left: true,
  top_right: true,
  bottom_left: true,
  bottom_right: true,
  none: true,
};

const OVERLAY_STYLES: Record<SlideOverlayStyle, true> = {
  faded: true,
  frosted: true,
  clear: true,
  duotone: true,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isPoint(value: unknown): value is SlidePoint | null {
  if (value === null) return true;
  return (
    isRecord(value) &&
    typeof value.x === 'number' &&
    Number.isFinite(value.x) &&
    typeof value.y === 'number' &&
    Number.isFinite(value.y)
  );
}

function isPhotoBox(value: unknown): value is SlidePhotoBox | null {
  if (value === null) return true;
  return (
    isRecord(value) &&
    ['x', 'y', 'w', 'h'].every(
      (key) => typeof value[key] === 'number' && Number.isFinite(value[key]),
    )
  );
}

/** The five arms of the background union, each with its own required fields. */
function isBackground(value: unknown): value is SlideBackground {
  if (!isRecord(value)) return false;
  switch (value.kind) {
    case 'color':
      return isString(value.color);
    case 'gradient':
      return isString(value.from) && isString(value.to);
    case 'asset':
    case 'generated':
      return isString(value.asset_path);
    case 'pattern':
      return isSlidePattern(value.pattern) && isString(value.ground) && isString(value.ink);
    default:
      return false;
  }
}

function isItem(value: unknown): value is SlideDraftItem {
  return (
    isRecord(value) &&
    isString(value.title) &&
    isString(value.subtitle) &&
    isString(value.value) &&
    (value.asset_path === null || isString(value.asset_path))
  );
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every(isString);
}

function isForm(value: unknown): value is StoredSlideDraftForm {
  if (!isRecord(value)) return false;
  return (
    isString(value.headline) &&
    isString(value.body) &&
    isString(value.eyebrow) &&
    isString(value.overlayStyle) &&
    Object.hasOwn(OVERLAY_STYLES, value.overlayStyle) &&
    typeof value.cardEnabled === 'boolean' &&
    isBackground(value.background) &&
    isString(value.logoPosition) &&
    Object.hasOwn(LOGO_POSITIONS, value.logoPosition) &&
    isPoint(value.textPosition) &&
    isString(value.layoutTemplate) &&
    Object.hasOwn(SLIDE_TEMPLATES, value.layoutTemplate) &&
    isPoint(value.logoPoint) &&
    isPhotoBox(value.photoBox) &&
    Array.isArray(value.items) &&
    value.items.every(isItem) &&
    (value.templateFields === undefined || isStringRecord(value.templateFields)) &&
    (value.pictureTreatment === undefined || isPictureTreatment(value.pictureTreatment)) &&
    (value.icon === undefined || value.icon === null || isString(value.icon))
  );
}

function isPayload(value: unknown): value is SlideDraftPayload {
  return isRecord(value) && isString(value.baseline) && isForm(value.form);
}

/** The editor's item rows as the draft stores them. */
export function slideDraftItems(items: readonly SlideItemRead[]): SlideDraftItem[] {
  return items.map((item) => ({
    title: item.title,
    subtitle: item.subtitle ?? '',
    value: item.value ?? '',
    asset_path: item.asset_path,
  }));
}

/**
 * The slide's own values: what the editor opens with when nothing was
 * recovered, and what "nothing has changed" means for the rest of the session.
 */
export function slideDraftForm(slide: SlideRead): SlideDraftForm {
  return {
    headline: slide.headline,
    body: slide.body ?? '',
    eyebrow: slide.eyebrow ?? '',
    overlayStyle: slide.theme.overlay_style,
    cardEnabled: slide.theme.card_enabled,
    background: slide.background,
    logoPosition: slide.logo_position,
    // Null means "the template places it": an unpinned slide starts there so
    // the preview shows what the next render will do.
    textPosition: slide.text_position_locked ? slide.text_position : null,
    layoutTemplate: slide.layout_template,
    pictureTreatment: readPictureTreatment(slide.picture_treatment),
    icon: slide.icon ?? null,
    logoPoint: slide.logo_point,
    photoBox: slide.photo_box,
    items: slideDraftItems(slide.items),
    templateFields: { ...slide.template_fields },
  };
}

/**
 * What the editor should open with. Call it from a lazy `useState` initialiser,
 * so recovered work is on screen from the first render.
 *
 * A draft written against another version of this slide is dropped from storage
 * rather than applied — silently, because the user cannot act on it and the
 * server's copy is the one they are looking at.
 */
export function restoreSlideDraft(slide: SlideRead): SlideDraftRestore {
  const pristine = slideDraftForm(slide);
  const key = slideDraftKey(slide.id);
  const stored = readDraft<SlideDraftPayload>(key, isPayload);
  if (stored === null) return { form: pristine, savedAt: null };

  if (stored.data.baseline !== slide.updated_at) {
    clearDraft(key);
    return { form: pristine, savedAt: null };
  }

  const form = stored.data.form;
  return {
    form: {
      ...form,
      templateFields: form.templateFields ?? { ...slide.template_fields },
      pictureTreatment: form.pictureTreatment ?? pristine.pictureTreatment,
      icon: form.icon === undefined ? pristine.icon : form.icon,
    },
    savedAt: stored.savedAt,
  };
}

/**
 * Mirrors the editor's live values into `localStorage`, debounced, and owns the
 * recovery notice's lifetime.
 *
 * Nothing here talks to the API. A draft is written on a timer and on nothing
 * else — no save on drop, no beacon on unload — because a render is the cost of
 * a save and only the user gets to spend it.
 */
export function useSlideDraft(
  slide: SlideRead,
  form: SlideDraftForm,
  /** From {@link restoreSlideDraft}, held by the caller across renders. */
  savedAt: string | null,
  /**
   * False for a view-only editor (a plan without the post editor): nothing is
   * written, and a draft already on file is left exactly as it was.
   */
  enabled = true,
): SlideDraftState {
  const key = slideDraftKey(slide.id);
  const storageKey = enabled ? key : null;
  const [isNoticeDismissed, setNoticeDismissed] = useState(false);

  // The baseline sits inside the payload, so it is part of what is compared:
  // once a save lands, the new slide is both the new baseline and the new
  // pristine value, and `useDraft` retires the draft on its own.
  useDraft<SlideDraftPayload>(
    storageKey,
    { baseline: slide.updated_at, form },
    { baseline: slide.updated_at, form: slideDraftForm(slide) },
    SLIDE_DRAFT_DEBOUNCE_MS,
  );

  const dismiss = useCallback(() => setNoticeDismissed(true), []);

  const clear = useCallback(() => {
    clearDraft(key);
    setNoticeDismissed(true);
  }, [key]);

  return {
    restoredAt: isNoticeDismissed ? null : savedAt,
    dismiss,
    discard: clear,
    clear,
  };
}
